<template>
    <div class="page-wrapper">
      <AppHeader />
  
      <div class="sell-car-view">
        <div class="sell-container">
          <h1 class="sell-title">{{ $t('sell_car_form.title') }}</h1>
          <p class="sell-subtitle">
            {{ $t('sell_car_form.subtitle') }}
          </p>
  
          <div v-if="success" class="success-message">
            <div class="message-icon">✓</div>
            <div class="message-text">
              <h3>{{ $t('sell_car_form.success_title') }}</h3>
              <p>{{ $t('sell_car_form.success_message') }}</p>
            </div>
          </div>
  
          <div v-if="error" class="error-message">
            <div class="message-icon">!</div>
            <div class="message-text">
              <h3>{{ $t('sell_car_form.error_title') }}</h3>
              <p>{{ error }}</p>
            </div>
          </div>
  
          <SellCarForm 
            v-if="!success"
            @submit="handleSubmit" 
            :loading="loading" 
          />
        </div>
      </div>
  
      <AppFooter />
    </div>
</template>
  
<script setup>
  import { ref } from 'vue'
  import axios from 'axios'
  import { useI18n } from 'vue-i18n'
  import AppHeader from '@/components/AppHeader.vue'
  import AppFooter from '@/components/AppFooter.vue'
  import SellCarForm from '@/components/SellCarForm.vue'
  
  const { t } = useI18n()
  const loading = ref(false)
  const error = ref(null)
  const success = ref(false)
  
  const handleSubmit = async (formData) => {
    try {
      loading.value = true
      error.value = null
      success.value = false
  
      const form = new FormData()
      for (const key in formData) {
        if (key === 'images') {
          formData.images.forEach(image => {
            form.append('images', image)
          })
        } else {
          form.append(key, formData[key])
        }
      }
  
      const response = await axios.post('/api/sell-cars', form)
  
      if (response.data.status === "email_sent") {
        success.value = true
      } else {
        error.value = response.data.message || t('sell_car_form.error_message')
      }
    } catch (err) {
      error.value = err.response?.data?.message || 
                    err.response?.data?.detail || 
                    err.message || 
                    t('sell_car_form.error_message')
    } finally {
      loading.value = false
    }
  }
  </script>
  
  <style scoped>
  
  .success-message, .error-message {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    border-radius: 8px;
    margin-bottom: 2rem;
  }
  
  .success-message {
    background-color: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  }
  
  .error-message {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
  }
  
  .message-icon {
    font-size: 1.5rem;
    font-weight: bold;
    margin-top: 0.2rem;
  }
  
  .message-text h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.2rem;
  }
  
  .message-text p {
    margin: 0;
    line-height: 1.5;
  }
  
  .page-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  
  .sell-car-view {
    background-color: #f8f9fa;
    flex: 1;
    padding: 12rem 1rem;
    display: flex;
    justify-content: center;
  }
  
  .sell-container {
    max-width: 800px;
    width: 100%;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 2.5rem;
    margin: 2rem 0;
  }
  
  .sell-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 1rem;
    text-align: center;
  }
  
  .sell-subtitle {
    font-size: 1.1rem;
    color: #666;
    margin-bottom: 2.5rem;
    text-align: center;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    .sell-container {
      padding: 1.5rem;
    }
  
    .sell-title {
      font-size: 2rem;
    }
  
    .sell-subtitle {
      font-size: 1rem;
    }
  
    .success-message, .error-message {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
  </style>
  